import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "What questions should I ask a SAINT® provider?",
    answer:
      "Ask: Do you use the Magnus Neuromodulation System? Do you use fMRI-guided targeting? Are you listed on the Magnus Medical provider locator? What does the evaluation include? What is the total cost? What insurance do you accept? What is the follow-up plan? Who provides the psychiatric evaluation? What happens if I don't improve?",
  },
  {
    question: "How can I tell if a clinic really offers SAINT®?",
    answer:
      "A clinic offering SAINT® should use the Magnus Neuromodulation System and fMRI-guided targeting. If a clinic says they offer 'accelerated TMS' or '5-day TMS' but does not mention the Magnus system or fMRI, they may be delivering an off-label accelerated schedule on a different device. Verify on the Magnus Medical provider locator.",
  },
  {
    question: "What should the evaluation include?",
    answer:
      "A SAINT® evaluation typically includes a psychiatric evaluation, review of your medication and treatment history, MRI safety screening, and a structural and functional MRI scan. The fMRI is used to compute your individualized stimulation target. Ask the clinic what their evaluation includes before starting.",
  },
];

export const pageQuestionsToAskProvider: LearnPageData = {
  slug: "questions-to-ask-a-saint-provider",
  title: "Questions to Ask a SAINT® Provider | Neuro Wellness",
  description:
    "Before starting SAINT® TMS, ask these key questions about the Magnus system, fMRI targeting, cost, insurance, evaluation, and follow-up care.",
  canonical: "/learn/questions-to-ask-a-saint-provider",
  h1: "Questions to Ask a SAINT® Provider",
  breadcrumbLabel: "Questions to Ask a SAINT® Provider",
  crumbs: baseCrumbs("Questions to Ask a SAINT® Provider"),
  shortAnswer:
    "Before starting SAINT® TMS, ask the provider: Do you use the Magnus Neuromodulation System? Do you use fMRI-guided targeting? Are you listed on the Magnus Medical provider locator? What does the evaluation include? What is the total cost? What insurance do you accept? What is the follow-up plan? Who provides the psychiatric evaluation? What happens if I don't improve?",
  keyTakeaways: [
    "Confirm the clinic uses the Magnus Neuromodulation System and fMRI targeting.",
    "Verify the clinic's listing on the Magnus Medical provider locator.",
    "Ask about the full evaluation, including MRI and psychiatric review.",
    "Understand the total cost and insurance coverage before starting.",
    "Ask about the follow-up and maintenance plan after the 5 days.",
  ],

  sections: [
    {
      heading: "About the Technology",
      body: (
        <>
          {P(
            "Ask: Do you use the Magnus Neuromodulation System with SAINT® Technology? Do you use resting-state fMRI to select the stimulation target? What neuronavigation system do you use? These questions confirm the clinic is delivering the FDA-cleared SAINT® protocol, not an off-label accelerated schedule.",
          )}
        </>
      ),
    },
    {
      heading: "About the Evaluation",
      body: (
        <>
          {P(
            "Ask: What does the evaluation include? Who performs the psychiatric evaluation? Do I need an MRI? How is the target chosen? How long before treatment can start? A proper SAINT® evaluation includes a psychiatric evaluation, MRI safety screening, and a structural and functional MRI scan.",
          )}
        </>
      ),
    },
    {
      heading: "About Cost and Insurance",
      body: (
        <>
          {P(
            "Ask: What is the total cost? What insurance do you accept? Do you verify benefits before treatment? Is prior authorization required? What are the self-pay options? SAINT® is billed under Category III CPT codes, and coverage varies by plan. Many patients self-pay.",
          )}
          {P(
            <>
              <a
                href="/learn/does-insurance-cover-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Learn about SAINT® insurance coverage
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "About Follow-Up",
      body: (
        <>
          {P(
            "Ask: What happens after the 5 days? What follow-up is recommended? What is the maintenance plan? What if I relapse? Can SAINT® be repeated? The research on long-term durability is limited, so a follow-up plan is important.",
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation: "Magnus Medical — SAINT® Provider Locator.",
      url: "https://www.magnusmedical.com/",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/questions-to-ask-a-saint-provider#webpage`,
        name: "Questions to Ask a SAINT® Provider",
        url: `${SITE_URL}/learn/questions-to-ask-a-saint-provider`,
        description: "Key questions to ask before starting SAINT TMS.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/learn/questions-to-ask-a-saint-provider#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("Questions to Ask a SAINT® Provider")),
    ],
  },
  relatedLinks: [
    {
      title: "Find a Verified SAINT® Provider",
      href: "/find-a-verified-saint-provider",
      description: "Our official Magnus Medical listing",
    },
    {
      title: "What Is a Verified SAINT® Provider?",
      href: "/learn/what-is-a-verified-saint-provider",
      description: "What verification means",
    },
    {
      title: "What to Expect During SAINT®",
      href: "/learn/what-to-expect-during-saint-tms",
      description: "The 5-day treatment guide",
    },
    {
      title: "SAINT® Cost",
      href: "/learn/saint-tms-cost",
      description: "Cost and insurance",
    },
  ],
};
